/* eslint-disable */
import React from "react";
import { supabase } from "./supabaseClient";

const C = {
  primary:"#1a6b3c", primaryPale:"#e8f5ee",
  amber:"#d97706",   amberPale:"#fffbeb",
  rose:"#e11d48",    rosePale:"#fef2f2",
  blue:"#2563eb",    bluePale:"#eff6ff",
  text:"#111827",    muted:"#6b7280",       border:"#d1fae5",
  white:"#ffffff",   shadow:"rgba(0,0,0,0.06)",
};

const isTeacherRole = (role) => {
  const r = (role||"").toLowerCase();
  return r.includes("teach") || r.includes("hifz");
};

const scoreColor = (score) => score==null?C.muted: score>=80?C.primary : score>=60?C.amber : C.rose;
const scoreBg    = (score) => score==null?"#f3f4f6": score>=80?C.primaryPale : score>=60?C.amberPale : C.rosePale;

function Bar({ pct, col }) {
  return (
    <div style={{background:"#f3f4f6",borderRadius:6,height:7,overflow:"hidden"}}>
      <div style={{width:`${Math.max(0,Math.min(100,pct))}%`,height:"100%",background:col,borderRadius:6,transition:"width .3s"}}/>
    </div>
  );
}

function MetricRow({ icon, label, pct, note, col }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
        <span style={{color:C.text,fontWeight:600}}>{icon} {label}</span>
        <span style={{color:C.muted}}>{pct==null?"No data":`${pct}%`}{note?` · ${note}`:""}</span>
      </div>
      <Bar pct={pct==null?0:pct} col={pct==null?"#d1d5db":col}/>
    </div>
  );
}

export function TeacherScorecardAdmin() {
  const [staff, setStaff] = React.useState([]);
  const [timetable, setTimetable] = React.useState([]);
  const [plans, setPlans] = React.useState([]);
  const [topicsByPlan, setTopicsByPlan] = React.useState({});
  const [conduct, setConduct] = React.useState([]);
  const [results, setResults] = React.useState([]);
  const [observations, setObservations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [sortBy, setSortBy] = React.useState("score");
  const [expanded, setExpanded] = React.useState(null);

  React.useEffect(()=>{
    (async () => {
      setLoading(true);
      const [{data:staffRows},{data:ttRows},{data:planRows},{data:conductRows},{data:resultRows},{data:obsRows}] = await Promise.all([
        supabase.from("staff").select("id,name,role,subject,status").eq("status","Active").order("name"),
        supabase.from("timetable").select("teacher_name,class,subject"),
        supabase.from("course_plans").select("*").order("created_at",{ascending:false}),
        supabase.from("staff_conduct").select("*").order("date",{ascending:false}),
        supabase.from("results").select("student_id,class,subject,marks_obtained,total_marks"),
        supabase.from("classroom_observations").select("teacher_name,score_lesson_planning,score_teaching_clarity,score_student_engagement,score_classroom_management,score_use_of_resources,score_assessment_questioning,score_pace_timing,overall_grade,date,observation_type").order("date",{ascending:false}),
      ]);
      setStaff((staffRows||[]).filter(s=>isTeacherRole(s.role)));
      setTimetable(ttRows||[]);
      setPlans(planRows||[]);
      setConduct(conductRows||[]);
      setResults(resultRows||[]);
      setObservations(obsRows||[]);
      if ((planRows||[]).length) {
        const { data: topicRows } = await supabase.from("course_plan_topics").select("*").in("plan_id", planRows.map(p=>p.id));
        const grouped = {};
        (topicRows||[]).forEach(t => { (grouped[t.plan_id] ||= []).push(t); });
        setTopicsByPlan(grouped);
      }
      setLoading(false);
    })();
  }, []);

  const CRITERIA_KEYS = ["score_lesson_planning","score_teaching_clarity","score_student_engagement","score_classroom_management","score_use_of_resources","score_assessment_questioning","score_pace_timing"];
  const GRADE_SCORE = {outstanding:4,good:3,requires_improvement:2,inadequate:1};

  const teacherData = React.useMemo(() => staff.map(s => {
    const taught = timetable.filter(t=>t.teacher_name===s.name);
    const classSubjectPairs = [...new Map(taught.map(t=>[`${t.class}__${t.subject}`,t])).values()];
    const classes = [...new Set(taught.map(t=>t.class))].filter(Boolean);
    const subjects = [...new Set(taught.map(t=>t.subject))].filter(Boolean);

    // Course plan completion
    const myPlans = plans.filter(p=>p.teacher_name===s.name);
    let totalTopics=0, completedTopics=0;
    myPlans.forEach(p => { const t = topicsByPlan[p.id]||[]; totalTopics+=t.length; completedTopics+=t.filter(x=>x.status==="completed").length; });
    const planPct = totalTopics>0 ? Math.round(completedTopics/totalTopics*100) : null;

    // Exam results in classes/subjects they teach
    const myResults = results.filter(r=>classSubjectPairs.some(p=>p.class===r.class && p.subject===r.subject));
    const resultPct = myResults.length
      ? Math.round(myResults.reduce((sum,r)=>sum+(r.total_marks>0?r.marks_obtained/r.total_marks*100:0),0)/myResults.length)
      : null;

    // Conduct
    const myConduct = conduct.filter(c=>c.staff_name===s.name);
    const commendations = myConduct.filter(c=>c.type==="commendation").length;
    const warnings       = myConduct.filter(c=>c.type==="warning").length;
    const disciplinary   = myConduct.filter(c=>c.type==="disciplinary").length;
    const conductPct = (myConduct.length===0) ? null
      : Math.max(0, Math.min(100, 100 + commendations*5 - warnings*10 - disciplinary*20));

    // Classroom observations for this teacher
    const myObs = observations.filter(o=>o.teacher_name===s.name);
    let obsPct = null;
    if (myObs.length) {
      const scores = myObs.flatMap(o=>CRITERIA_KEYS.map(k=>o[k]).filter(v=>v!=null));
      if (scores.length) {
        const avgCriteria = scores.reduce((a,b)=>a+b,0)/scores.length;
        obsPct = Math.round((avgCriteria-1)/3*100);
      } else {
        // fall back to overall_grade if no criteria rated
        const gradeScores = myObs.map(o=>GRADE_SCORE[o.overall_grade]).filter(Boolean);
        if (gradeScores.length) obsPct = Math.round((gradeScores.reduce((a,b)=>a+b,0)/gradeScores.length-1)/3*100);
      }
    }
    const latestObs = myObs[0]||null;

    const parts = [planPct, resultPct, conductPct, obsPct].filter(v=>v!=null);
    const score = parts.length ? Math.round(parts.reduce((a,b)=>a+b,0)/parts.length) : null;

    return { staff:s, classes, subjects, planPct, resultPct, conductPct, obsPct, score, commendations, warnings, disciplinary, myPlans, myConduct:myConduct.slice(0,5), obsCount:myObs.length, latestObs };
  }), [staff, timetable, plans, topicsByPlan, conduct, results, observations]);

  const filtered = teacherData
    .filter(t => !search.trim() || t.staff.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      const key = sortBy==="score"?"score":sortBy==="plan"?"planPct":sortBy==="results"?"resultPct":"conductPct";
      const av = a[key]==null?-1:a[key], bv = b[key]==null?-1:b[key];
      return bv-av;
    });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{background:C.white,borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:17,color:C.primary,marginBottom:4}}>🏆 Teacher Scorecard</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
          A composite read on teaching quality — course plan delivery, student results, and conduct record. Visible to Admin only.
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input placeholder="🔎 Search teacher" value={search} onChange={e=>setSearch(e.target.value)}
            style={{padding:"8px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12,minWidth:200}}/>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{padding:"8px 10px",borderRadius:7,border:`1px solid ${C.border}`,fontSize:12}}>
            <option value="score">Sort: Overall Score</option>
            <option value="plan">Sort: Course Plan Completion</option>
            <option value="results">Sort: Exam Results</option>
            <option value="conduct">Sort: Conduct</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{color:C.muted,fontSize:13,padding:16}}>Loading…</div>
      ) : filtered.length===0 ? (
        <div style={{background:C.white,borderRadius:12,padding:30,textAlign:"center",color:C.muted,fontSize:13,border:`1px solid ${C.border}`}}>
          No teaching staff found.
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map(t => {
            const isOpen = expanded===t.staff.id;
            return (
              <div key={t.staff.id} style={{background:C.white,borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                <div onClick={()=>setExpanded(isOpen?null:t.staff.id)}
                  style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",cursor:"pointer"}}>
                  <div>
                    <div style={{fontWeight:800,fontSize:14,color:C.text}}>{t.staff.name}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                      {t.staff.role||"Teacher"} {t.classes.length>0 && `· ${t.classes.join(", ")}`} {t.subjects.length>0 && `· ${t.subjects.join(", ")}`}
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{background:scoreBg(t.score),color:scoreColor(t.score),border:`1px solid ${scoreColor(t.score)}33`,borderRadius:10,padding:"6px 14px",textAlign:"center",minWidth:64}}>
                      <div style={{fontWeight:900,fontSize:18,lineHeight:1}}>{t.score==null?"—":t.score}</div>
                      <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase"}}>Score</div>
                    </div>
                    <span style={{color:C.muted,fontSize:12}}>{isOpen?"▲":"▼"}</span>
                  </div>
                </div>

                {isOpen && (
                  <div style={{padding:"4px 18px 18px",display:"flex",flexDirection:"column",gap:14}}>
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      <MetricRow icon="📘" label="Course Plan Completion" pct={t.planPct} col={C.blue}
                        note={t.myPlans.length ? `${t.myPlans.length} plan(s)` : null}/>
                      <MetricRow icon="📊" label="Exam Results (their classes)" pct={t.resultPct} col={C.primary}/>
                      <MetricRow icon="📌" label="Conduct" pct={t.conductPct} col={C.amber}
                        note={t.myConduct.length ? `${t.commendations} commendation, ${t.warnings} warning, ${t.disciplinary} disciplinary` : null}/>
                      <MetricRow icon="🏫" label="Lesson Observations" pct={t.obsPct} col={"#7c3aed"}
                        note={t.obsCount ? `${t.obsCount} observation(s)${t.latestObs ? ` · Latest: ${t.latestObs.overall_grade?.replace(/_/g," ")}` : ""}` : null}/>
                    </div>

                    {t.myConduct.length>0 && (
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:6}}>Recent Conduct Entries</div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {t.myConduct.map(c=>(
                            <div key={c.id} style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px",fontSize:12,display:"flex",justifyContent:"space-between",gap:8}}>
                              <span style={{color:C.text}}>{c.title}</span>
                              <span style={{color:C.muted,whiteSpace:"nowrap"}}>{c.date}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {t.myPlans.length>0 && (
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:6}}>Course Plans</div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {t.myPlans.map(p=>{
                            const topics = topicsByPlan[p.id]||[];
                            const pct = topics.length ? Math.round(topics.filter(x=>x.status==="completed").length/topics.length*100) : 0;
                            return (
                              <div key={p.id} style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px",fontSize:12,display:"flex",justifyContent:"space-between",gap:8}}>
                                <span style={{color:C.text}}>{p.subject_name} · {p.class} ({p.term_name})</span>
                                <span style={{color:C.muted}}>{pct}% complete</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
